'use client';

import { useMemo, useState } from 'react';

import {
  useBreakingCategories,
  useBreakingEventsListInfinite,
  useBreakingEventsMeta,
} from '@/services/query';
import type { BreakingEventsListParams, BreakingNewsCategory } from '@/types/breaking.types';

export const useBreaking = () => {
  // State
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);

  // Fetch categories
  const {
    data: apiCategories = [],
    isLoading: isCategoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useBreakingCategories();

  // Add a frontend "All" category at the beginning of the list
  const allCategory: BreakingNewsCategory = {
    name: 'All',
    slug: 'all',
    description: 'All breaking news',
    tagId: null,
  };
  const categories: BreakingNewsCategory[] = [allCategory, ...apiCategories];

  const eventsListParams: BreakingEventsListParams = {
    limit: 20,
    offset: 0,
    order: 'createdAt',
    ascending: false,
    type: 'breaking-news' as const,
    window: '24h',
    tag_id: selectedTagId,
  };

  const {
    data,
    isLoading: isEventsLoading,
    error: eventsError,
    refetch: refetchEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBreakingEventsListInfinite(eventsListParams);

  const markets = useMemo(() => data?.pages.flatMap((page) => page.data) || [], [data]);

  // Shares the same request/cache entry as useBreakingEventsList above (same
  // params -> same queryKey), just projected to `meta` for the "updated at" stamp.
  const { data: eventsMeta } = useBreakingEventsMeta(eventsListParams);

  // Sort markets by absolute percent change descendingly — matches the
  // metric the backend uses to select/rank this window of movers.
  const sortedMarkets = useMemo(() => {
    return [...markets].sort((a, b) => {
      const absA = Math.abs(a?.percent_change ?? 0);
      const absB = Math.abs(b?.percent_change ?? 0);
      return absB - absA;
    });
  }, [markets]);

  // Risers/fallers among the currently-loaded movers (real, not fabricated —
  // there is no total-universe breakdown endpoint, only this ranked window).
  const { risersCount, fallersCount } = useMemo(() => {
    let risers = 0;
    let fallers = 0;
    for (const m of sortedMarkets) {
      const change = m?.percent_change ?? 0;
      if (change > 0) risers += 1;
      else if (change < 0) fallers += 1;
    }
    return { risersCount: risers, fallersCount: fallers };
  }, [sortedMarkets]);

  // Combined error state
  const error = categoriesError || eventsError || null;

  // Handlers
  const handleCategoryClick = (tagId: string | null) => {
    setSelectedTagId(tagId);
  };

  const refetch = () => {
    refetchCategories();
    refetchEvents();
  };

  return {
    // Data
    categories,
    markets: sortedMarkets,
    selectedTagId,
    updatedAt: eventsMeta?.generated_at,
    risersCount,
    fallersCount,

    // States
    isCategoriesLoading,
    isEventsLoading,
    error,

    // Handlers
    handleCategoryClick,
    refetch,
    fetchNextPage,

    // Pagination
    pagination: {
      hasNextPage,
      isFetchingNextPage,
    },
  };
};
